/**
 * @fileoverview Edit mode content for user details modal
 */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import { intl, LanguageBaseContext, DmsPermissions, DmsTextFieldPhone } from "dmsbasereact";
import {
  Box, Grid, Chip, ListItemText, TextField,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import { getBrazilianStateOptions } from "../../constants/BrazilianStates";
import { LANGUAGE_OPTIONS, STATUS_OPTIONS, ACCOUNT_STATUS_OPTIONS, DOCTOR_ACCOUNT_STATUS_OPTIONS } from "../../constants/UserManagementConstants";
import { DmsInfoCard } from "../DmsDetailModal/DmsInfoCard";
import { DmsInstitutionSelect } from "../DmsInstitutionSelect";
import { mapRolesToUserTypes } from "../../constants/UserType";
import { MultiCrmInput } from "../MultiCrmInput";
import { DmsTextFieldCep } from "../DmsTextFieldCep";
import { DmsTextFieldCpf, isValidCPF, CpfMask } from "../DmsTextFieldCpf/DmsTextFieldCpf";
import { UserDocuments } from "./UserDocuments";
import { Permissions } from "../../constants/Permissions";

/**
 * Edit mode content for editing user details
 * Receives `data`, `formData`, `onFieldChange` from DmsDetailModal
 * and `availableRoles` via editContentProps
 */
export const EditModeContent = ({ data,
  formData,
  onFieldChange,
  availableRoles = [],
  userType = null}) => {
  const { language: systemLanguage } = useContext(LanguageBaseContext);
  const user = data;
  return (
    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
      {/* Basic Information */}
      <DmsInfoCard icon={<PersonIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={intl.get("Name").d("Name")}
              value={formData.user_name || ""}
              onChange={(e) => onFieldChange("user_name", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={intl.get("Email").d("Email")}
              value={formData.user_email || ""}
              onChange={(e) => onFieldChange("user_email", e.target.value)}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid item xs={6}>
            <DmsTextFieldPhone
              label={intl.get("Phone").d("Phone")}
              value={formData.user_phone || ""}
              onChange={(e) => onFieldChange("user_phone", e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("Language").d("Language")}</InputLabel>
              <Select
                value={formData.user_language || systemLanguage}
                label={intl.get("Language").d("Language")}
                onChange={(e) => onFieldChange("user_language", e.target.value)}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* For external users: show account_status (editable) + status_reason together */}
          {/* For internal users: show editable status select */}
          {user.is_external && user.person ? (
            <>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{intl.get("AccountStatus").d("Account Status")}</InputLabel>
                  <Select
                    value={formData.account_status ?? user.person.account_status ?? 5}
                    label={intl.get("AccountStatus").d("Account Status")}
                    onChange={(e) => onFieldChange("account_status", e.target.value)}
                  >
                    {(user.doctor ? DOCTOR_ACCOUNT_STATUS_OPTIONS : ACCOUNT_STATUS_OPTIONS).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {intl.get(opt.label).d(opt.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={intl.get("StatusReason").d("Status Reason")}
                  value={formData.status_reason || ""}
                  onChange={(e) => onFieldChange("status_reason", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{intl.get("Status").d("Status")}</InputLabel>
                <Select
                  value={formData.status ?? 1}
                  label={intl.get("Status").d("Status")}
                  onChange={(e) => onFieldChange("status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {intl.get(opt.label).d(opt.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DmsInfoCard>

      {/* Roles */}
      <DmsInfoCard icon={<SecurityIcon />} title={intl.get("Roles").d("Roles")}>
        <FormControl fullWidth size="small">
          <InputLabel>{intl.get("Roles").d("Roles")}</InputLabel>
          <Select
            multiple
            value={formData.roles || []}
            label={intl.get("Roles").d("Roles")}
            onChange={(e) => onFieldChange("roles", e.target.value)}
            input={<OutlinedInput label={intl.get("Roles").d("Roles")} />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={intl.get(`RoleName.${value}`).d(value)} size="small" />
                ))}
              </Box>
            )}
          >
            {availableRoles.map((role) => (
              <MenuItem key={role.pk} value={role.role_name}>
                <Checkbox checked={(formData.roles || []).indexOf(role.role_name) > -1} />
                <ListItemText primary={intl.get(`RoleName.${role.role_name}`).d(role.role_name)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DmsInfoCard>

      {/* External user specific sections */}
      {user.is_external && (
        <>
          {/* Person Information */}
          {user.person && (
            <DmsInfoCard icon={<PersonIcon />} title={intl.get("PersonInfo").d("Person Information")}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DmsTextFieldCpf
                    label={intl.get("CPF").d("CPF")}
                    value={CpfMask(formData.cpf || "")}
                    onChange={(e) => onFieldChange("cpf", e.target.value)}
                    fullWidth
                    size="small"
                    disabled={isValidCPF(user.person?.cpf || "")}
                    helperText={
                      !isValidCPF(user.person?.cpf || "")
                        ? intl.get("InvalidCPF.EditHint").d("Current CPF is invalid. Enter the correct CPF.")
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            </DmsInfoCard>
          )}

          {/* Doctor Information */}
          {user.doctor && (
            <DmsInfoCard icon={<LocalHospitalIcon />} title={intl.get("DoctorInfo").d("Doctor Information")}>
              <MultiCrmInput
                crms={(() => {
                  // Build CRM array: primary first, then additional CRMs
                  const allCrms = [];
                  if (formData.crm && formData.crm_state) {
                    allCrms.push({ crm: formData.crm, crm_state: formData.crm_state });
                  }
                  if (formData.crms && Array.isArray(formData.crms)) {
                    formData.crms.forEach(c => {
                      // Avoid duplicates with primary
                      if (!(c.crm === formData.crm && c.crm_state === formData.crm_state)) {
                        allCrms.push(c);
                      }
                    });
                  }
                  return allCrms;
                })()}
                onChange={(updatedCrms) => {
                  // First CRM is primary, rest are additional
                  if (updatedCrms.length > 0) {
                    onFieldChange("crm", updatedCrms[0].crm);
                    onFieldChange("crm_state", updatedCrms[0].crm_state);
                    onFieldChange("crms", updatedCrms.slice(1));
                  } else {
                    onFieldChange("crm", "");
                    onFieldChange("crm_state", "");
                    onFieldChange("crms", []);
                  }
                }}
                required
              />
            </DmsInfoCard>
          )}

          {/* Pharmacist Information */}
          {user.pharmacist && (
            <DmsInfoCard icon={<LocalPharmacyIcon />} title={intl.get("PharmacistInfo").d("Pharmacist Information")}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={intl.get("CRF").d("CRF")}
                    value={formData.crf || ""}
                    onChange={(e) => onFieldChange("crf", e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{intl.get("CRFState").d("CRF State")}</InputLabel>
                    <Select
                      value={formData.crf_state || ""}
                      label={intl.get("CRFState").d("CRF State")}
                      onChange={(e) => onFieldChange("crf_state", e.target.value)}
                    >
                      {getBrazilianStateOptions().map(({ value: code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DmsInfoCard>
          )}

          {/* Address Information */}
          {["doctor", "pharmacist", "person", "distributor_responsible"].includes(userType?.toLowerCase()) && (
            <DmsInfoCard icon={<HomeIcon />} title={intl.get("AddressInfo").d("Address Information")}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <DmsTextFieldCep
                    name="zip_code"
                    label={intl.get("ZipCode").d("Zip Code")}
                    value={formData.zip_code || ""}
                    onChange={(e) => onFieldChange("zip_code", e.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label={intl.get("Address").d("Address")}
                    value={formData.address || ""}
                    onChange={(e) => onFieldChange("address", e.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={intl.get("Number").d("Number")}
                    value={formData.address_number || ""}
                    onChange={(e) => onFieldChange("address_number", e.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={intl.get("City").d("City")}
                    value={formData.city || ""}
                    onChange={(e) => onFieldChange("city", e.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>{intl.get("State").d("State")}</InputLabel>
                    <Select
                      value={formData.address_state || ""}
                      label={intl.get("State").d("State")}
                      onChange={(e) => onFieldChange("address_state", e.target.value)}
                    >
                      {getBrazilianStateOptions().map(({ value: code, label }) => (
                        <MenuItem key={code} value={code}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DmsInfoCard>
          )}

          {/* Institutions */}
          {["doctor", "pharmacist", "person", "distributor_responsible"].includes(userType?.toLowerCase()) && (
              <DmsInfoCard icon={<BusinessIcon />} title={intl.get("Institutions").d("Institutions")}>
                  <DmsInstitutionSelect
                      value={formData.institution_pks || []}
                      onChange={(value) => {
                          // All user types now support multiple institutions
                          onFieldChange("institution_pks", value);
                          // Also update institution_pk for backwards compatibility
                          onFieldChange("institution_pk", Array.isArray(value) && value.length > 0 ? value[0] : null);
                      }}
                      userTypes={mapRolesToUserTypes(formData.roles || [])}
                      multiple={true}
                      fullWidth
                      size="small"
                  />
              </DmsInfoCard>
          )}

          {/* Documents - shown for pharmacists and institution responsibles (not doctors) when user has USER_DOCUMENTS_UPLOAD permission */}
          {user.person && userType?.toLowerCase() !== 'doctor' && (
            <DmsPermissions permissions={Permissions.USER_DOCUMENTS_UPLOAD}>
              <UserDocuments personPk={user.person.pk} allowUpload={true} userType={userType} />
            </DmsPermissions>
          )}
        </>
      )}
    </Box>
  );
};

EditModeContent.propTypes = {
  data: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  availableRoles: PropTypes.array,
  userType: PropTypes.string,
};

